import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  api,
  canSee,
  homeViewFor,
  pathForView,
  resetPasswordWithLink,
  ssoProviders,
  ssoStartPath,
  toRole,
  viewForPath,
  type ApiError,
  type LoginOutcome,
  type SessionGrant,
  type TwofaEnrollStart,
} from '@aithora/shared';
import { API_BASE_URL, useSession } from '../session/SessionContext';
import { readTrustToken, takeSsoReturn, writeTrustToken } from '../auth/signinExtras';
import { Wordmark } from '../shell/SideNav';
import { getPasskeyAssertion, passkeysSupported } from '../auth/webauthn';
import { fromFor } from '../session/lastUser';

type Step =
  | { kind: 'credentials' }
  | { kind: 'reset'; token: string }
  | Exclude<LoginOutcome, { kind: 'session' }>;

/** Where to go after signing in: the page they asked for if their role may open it. */
function landingPath(grantRole: string, from: string | undefined): string {
  const role = toRole(grantRole);
  const asked = from ? viewForPath(from) : null;
  if (asked && canSee(role, asked)) return pathForView(asked);
  const home = homeViewFor(role);
  return home ? pathForView(home) : '/no-access';
}

function messageOf(e: unknown): string {
  const err = e as ApiError;
  if (err && typeof err.message === 'string' && err.message) return err.message;
  return 'Something went wrong. Try again.';
}

export function SignIn() {
  const { session, client, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { from?: string; fromUser?: string | null } | null;
  // An accepted invite whose role must use two-factor arrives here mid-enrolment.
  const inviteEnroll = (location.state as { enroll?: { pendingToken: string; expiresAt: string } } | null)?.enroll;

  // A password reset link lands here with ?reset=<token> (POST /admin/users/:id/reset-link).
  const resetToken = new URLSearchParams(location.search).get('reset');
  const [step, setStep] = useState<Step>(
    resetToken
      ? { kind: 'reset', token: resetToken }
      : inviteEnroll
        ? { kind: 'second_factor', mode: 'enroll', factors: [], pendingToken: inviteEnroll.pendingToken, expiresAt: inviteEnroll.expiresAt }
        : { kind: 'credentials' },
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);
  const [providers, setProviders] = useState<Array<{ id: string; label: string }>>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [enrollment, setEnrollment] = useState<TwofaEnrollStart | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Single sign-on (Google, Microsoft, SAML): the options the server has switched on.
  useEffect(() => {
    let live = true;
    ssoProviders(client).then((r) => live && setProviders(r.providers ?? []), () => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  // Back from single sign-on: the session (or why not) the callback left in the URL.
  useEffect(() => {
    const r = takeSsoReturn();
    if (!r) return;
    if (r.kind === 'error') setError(r.message);
    else void run(async () => finish(r.grant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus to the new heading each step, so a screen reader hears where it is.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step.kind]);

  // Enrolment: fetch the new secret once when that step opens.
  useEffect(() => {
    if (step.kind !== 'second_factor' || step.mode !== 'enroll' || enrollment) return;
    let live = true;
    api.twofaEnroll(client, { body: { pendingToken: step.pendingToken } })
      .then((r) => live && setEnrollment(r))
      .catch((e) => live && setError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [step, enrollment, client]);

  if (session) return <Navigate to={landingPath(session.user.rawRole, fromFor(session.user.id, navState))} replace />;

  const finish = async (grant: SessionGrant) => {
    // "Trust this device": keep the token the server issued, for this username or email.
    if (grant.trustDeviceToken) writeTrustToken(email, grant.trustDeviceToken);
    await signIn(grant);
    navigate(landingPath(grant.role, fromFor(grant.userId, navState)), { replace: true });
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const handleOutcome = async (o: LoginOutcome) => {
    if (o.kind === 'session') return finish(o.grant);
    setCode('');
    setStep(o);
  };

  const submitCredentials = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your username or email and your password.');
      return;
    }
    const trustDeviceToken = readTrustToken(email);
    void run(async () => {
      const o = await api.login(client, { email: email.trim(), password, ...(trustDeviceToken ? { trustDeviceToken } : {}) });
      // A trust token that no longer works (expired or revoked) is forgotten.
      if (trustDeviceToken && o.kind === 'second_factor') writeTrustToken(email, null);
      await handleOutcome(o);
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind === 'legacy_mfa') {
      void run(async () => handleOutcome(await api.login(client, { email: email.trim(), password, mfaCode: code.trim() })));
      return;
    }
    if (step.kind !== 'second_factor') return;
    const pendingToken = step.pendingToken;
    if (step.mode === 'enroll') {
      void run(async () => {
        const r = await api.twofaVerifyEnrollment(client, { body: { pendingToken, code: code.trim() } });
        if (r.token && r.userId && r.role) {
          await finish({ ok: true, token: r.token, userId: r.userId, role: r.role, name: r.name ?? null, organizationId: r.organizationId ?? null, expiresAt: r.expiresAt ?? '' });
        } else {
          setStep({ kind: 'credentials' });
          setError('Two-factor is set up. Sign in again to continue.');
        }
      });
      return;
    }
    const trust = trustDevice ? { trustDevice: true, deviceName: navigator.userAgent.slice(0, 120) } : {};
    const body = useBackup ? { pendingToken, backupCode: code.trim(), ...trust } : { pendingToken, code: code.trim(), ...trust };
    void run(async () => finish(await api.twofaChallenge(client, { body })));
  };

  const usePasskey = () => {
    if (step.kind !== 'second_factor') return;
    const pendingToken = step.pendingToken;
    void run(async () => {
      const opts = await api.passkeyAuthOptions(client, { body: { pendingToken } });
      const response = await getPasskeyAssertion(opts);
      const trust = trustDevice ? { trustDevice: true, deviceName: navigator.userAgent.slice(0, 120) } : {};
      await finish(await api.passkeyAuthVerify(client, { body: { pendingToken, response, ...trust } }));
    });
  };

  const submitNewPassword = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'password_change') return;
    if (newPassword.length < 10) return setError('Use at least 10 characters.');
    if (newPassword !== confirmPassword) return setError('The two passwords do not match.');
    const changeToken = step.changeToken;
    void run(async () => finish(await api.changePassword(client, { body: { changeToken, newPassword } })));
  };

  const submitReset = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'reset') return;
    if (newPassword.length < 10) return setError('Use at least 10 characters.');
    if (newPassword !== confirmPassword) return setError('The two passwords do not match.');
    const changeToken = step.token;
    void run(async () => {
      await resetPasswordWithLink(client, { body: { changeToken, newPassword } });
      setNewPassword('');
      setConfirmPassword('');
      navigate('/signin', { replace: true });
      setStep({ kind: 'credentials' });
      setNotice('Your new password is set. Sign in with it.');
    });
  };

  const startOver = () => {
    setStep({ kind: 'credentials' });
    setEnrollment(null);
    setCode('');
    setError(null);
  };

  const errorLine = error ? (
    <p className="signin__error" role="alert">
      {error}
    </p>
  ) : null;

  return (
    <div className="signin">
      <div className="signin__card">
        <Wordmark />

        {step.kind === 'credentials' && (
          <form onSubmit={submitCredentials} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Sign in to the console
            </h1>
            <label className="field">
              <span className="field__label">Username or email</span>
              <input className="field__input" name="username" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field__label">Password</span>
              <input className="field__input" name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {notice && !error && (
              <p className="signin__hint" role="status">
                {notice}
              </p>
            )}
            {errorLine}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            {providers.length > 0 && (
              <>
                <div className="signin__or" aria-hidden="true">
                  or
                </div>
                {providers.map((p) => (
                  <a key={p.id} className="btn btn--secondary" href={API_BASE_URL + ssoStartPath(p.id)}>
                    {'Continue with ' + p.label}
                  </a>
                ))}
              </>
            )}
            <div className="signin__links">
              <button type="button" className="linkbtn" onClick={() => navigate('/request-access')}>
                New to Aithora? Request access
              </button>
            </div>
          </form>
        )}

        {step.kind === 'second_factor' && step.mode === 'challenge' && (
          <form onSubmit={submitCode} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Confirm it is you
            </h1>
            {step.factors.includes('passkey') && (
              <>
                <p className="signin__lead">Use the passkey on this device, or a code from your authenticator app.</p>
                <button type="button" className="btn btn--primary" onClick={usePasskey} disabled={busy || !passkeysSupported()}>
                  Use your passkey
                </button>
                {!passkeysSupported() && <p className="signin__hint">This browser cannot use passkeys. Use a code instead.</p>}
                <div className="signin__or" aria-hidden="true">
                  or
                </div>
              </>
            )}
            <label className="field">
              <span className="field__label">{useBackup ? 'Backup code' : '6-digit code from your authenticator app'}</span>
              <input
                className="field__input field__input--code"
                name="code"
                inputMode={useBackup ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </label>
            <label className="signin__check">
              <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} /> Trust this device for 30 days
            </label>
            {errorLine}
            <button type="submit" className={`btn ${step.factors.includes('passkey') ? 'btn--secondary' : 'btn--primary'}`} disabled={busy || !code.trim()}>
              Verify code
            </button>
            <div className="signin__links">
              <button type="button" className="linkbtn" onClick={() => { setUseBackup((b) => !b); setCode(''); }}>
                {useBackup ? 'Use an authenticator code' : 'Use a backup code instead'}
              </button>
              <button type="button" className="linkbtn" onClick={startOver}>
                Start over
              </button>
            </div>
          </form>
        )}

        {step.kind === 'second_factor' && step.mode === 'enroll' && (
          <form onSubmit={submitCode} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Set up two-factor sign-in
            </h1>
            <p className="signin__lead">Your role needs a second step at sign-in. Add this key to your authenticator app, then enter the code it shows.</p>
            {!enrollment && !error && <p className="signin__hint" aria-busy="true">Preparing your key{'…'}</p>}
            {enrollment && (
              <>
                <div className="field">
                  <span className="field__label">Key for your authenticator app</span>
                  <code className="signin__secret">{enrollment.secret}</code>
                  <a className="signin__hint" href={enrollment.otpauth}>
                    Open in an authenticator app on this device
                  </a>
                </div>
                {enrollment.backupCodes.length > 0 && (
                  <div className="field">
                    <span className="field__label">Backup codes. Save these now: they are shown only once.</span>
                    <ul className="signin__codes">
                      {enrollment.backupCodes.map((c) => (
                        <li key={c}>
                          <code>{c}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <label className="field">
                  <span className="field__label">6-digit code</span>
                  <input className="field__input field__input--code" name="code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
                </label>
              </>
            )}
            {errorLine}
            <button type="submit" className="btn btn--primary" disabled={busy || !enrollment || !code.trim()}>
              Turn on two-factor
            </button>
            <div className="signin__links">
              <button type="button" className="linkbtn" onClick={startOver}>
                Start over
              </button>
            </div>
          </form>
        )}

        {step.kind === 'legacy_mfa' && (
          <form onSubmit={submitCode} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Confirm it is you
            </h1>
            <p className="signin__lead">{step.message}</p>
            {step.enrollment && (
              <div className="field">
                <span className="field__label">Key for your authenticator app</span>
                <code className="signin__secret">{step.enrollment.secret}</code>
              </div>
            )}
            <label className="field">
              <span className="field__label">6-digit code</span>
              <input className="field__input field__input--code" name="code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
            </label>
            {errorLine}
            <button type="submit" className="btn btn--primary" disabled={busy || !code.trim()}>
              Verify code
            </button>
            <div className="signin__links">
              <button type="button" className="linkbtn" onClick={startOver}>
                Start over
              </button>
            </div>
          </form>
        )}

        {step.kind === 'reset' && (
          <form onSubmit={submitReset} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Choose a new password
            </h1>
            <p className="signin__lead">Your team sent you a reset link. Choose a new password, then sign in with it.</p>
            <label className="field">
              <span className="field__label">New password</span>
              <input className="field__input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field__label">New password again</span>
              <input className="field__input" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </label>
            {errorLine}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              Save new password
            </button>
            <div className="signin__links">
              <button type="button" className="linkbtn" onClick={() => { navigate('/signin', { replace: true }); startOver(); }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {step.kind === 'password_change' && (
          <form onSubmit={submitNewPassword} noValidate>
            <h1 className="signin__title" tabIndex={-1} ref={headingRef}>
              Choose a new password
            </h1>
            <p className="signin__lead">You signed in with a temporary password. Choose your own to continue.</p>
            <label className="field">
              <span className="field__label">New password</span>
              <input className="field__input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field__label">New password again</span>
              <input className="field__input" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </label>
            {errorLine}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              Save and sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
